<template>
  <div>
    <wc-page-title title='Reconciliation Detail'>
      <template v-slot:rightSide v-if='rec'>
        <v-icon :color="rec.is_locked ? 'red' : 'grey'">mdi-lock</v-icon>
         <wc-burger-menu>
          <v-list-item @click='toggleLock' :disabled='rec.archived'>
            <v-list-item-icon>
              <v-icon color='red'>mdi-lock</v-icon>
            </v-list-item-icon>
            <v-list-item-title v-if='rec.is_locked'>UN-Lock Reconciliation</v-list-item-title>
            <v-list-item-title v-else>Lock Reconciliation</v-list-item-title>
          </v-list-item>
        </wc-burger-menu>
      </template>
    </wc-page-title>
    <v-container v-if='rec'>
      <v-row>
        <v-col cols='12' md='4'>
          <wc-data-box
            :title='toolbarData.dataBox.basics.title'
            :titleIcon='toolbarData.dataBox.basics.icon'
            :titleIconColor='toolbarData.dataBox.basics.iconColor'
            :showEdit='!rec.is_locked'
            @edit='editRec'
          >
            <wc-two-column-table :items='basicsData'></wc-two-column-table>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='4'>
          <v-simple-table dense>
            <thead></thead>
            <tbody>
              <tr>
                <td :class='labelClass'>CPM Opening Balance:</td>
                <td :class='valueClass'>{{ rec.opening_balance | decAccounting }}</td>
              </tr>
              <tr>
                <td :class='labelClass'>
                  <span v-if='rec.account_obj.is_credit_card'>Less Debits</span>
                  <span v-else>Add Debits</span>
                </td>
                <td :class='valueClass'>{{ totalDebits | decAccounting }}</td>
              </tr>
              <tr>
                <td :class='labelClass'>
                  <span v-if='rec.account_obj.is_credit_card'>Add Credits</span>
                  <span v-else>Less Credits</span>
                </td>
                <td :class='valueClass'>{{ totalCredits | decAccounting }}</td>
              </tr>
              <tr>
                <td style='border-top: solid 1px black' :class='labelClass'>CPM End Balance</td>
                <td style='border-top: solid 1px black' :class='valueClass'>{{ cpmBalance | decAccounting }}</td>
              </tr>
              <tr>
                <td :class='labelClass'>Statement End Balance</td>
                <td :class='valueClass'>{{ rec.end_balance | decAccounting }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
        <v-col cols='12' md='4'>
          <wc-data-box title='Difference'>
            <v-card-text class='text-center'>
              <span :class="diff !== 0 ? 'display-2 error--text' : 'display-2 success--text'">{{ diff | decAccounting }}</span><br>
              <small>(should be zero)</small>
            </v-card-text>
          </wc-data-box>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <wc-data-box title='Transactions to Include' :loading='isLoading'>
            <v-simple-table dense loading>
              <thead>
                <tr class='overline'>
                  <th>Date</th>
                  <th>Description</th>
                  <th width='125' class='text-right'>Debits</th>
                  <th width='125' class='text-right'>Credit</th>
                  <th width='60'></th>
                </tr>
              </thead>

              <tbody v-if='rec'>
                <rec-line
                  v-for='(line, index) in lines'
                  :key='index'
                  :line='line'
                  :rec='rec'
                  @updateLine='updateLine'
                  @lineClick='viewLineJE(line)'
                ></rec-line>
              </tbody>
            </v-simple-table>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>

    <reconciliation-tracker
      v-if='rec'
      :reconciliation='rec'
      ref='actionTracker'
    ></reconciliation-tracker>

    <wc-dialog
      :showDialog='showDialog'
      :title='toolbarData.dataBox.basics.title'
      :icon='toolbarData.dataBox.basics.icon'
      :disableButtons='!isFormValid'
      :loading='isSaving'
      width='400'
      @cancel='showDialog = false'
      @save='updateRec'
    >
      <v-form v-model='isFormValid'>
        <wc-datepicker
          v-model='recCopy.to_date'
          :min='recCopy.from_date'
          label='Date to'
          required
        ></wc-datepicker>
        <v-text-field
          prepend-icon='mdi-currency-usd'
          label='Statement Ending balance'
          :rules='[rules.decimal]'
          hint='Bank or Credit Card statement'
          persistent-hint
          v-model='recCopy.end_balance'
        ></v-text-field>
      </v-form>
    </wc-dialog>  </div>
</template>

<script>
import trackerAPI from '@/api/tracker';
import reconciliationsAPI from '@/api/accounting';
import ReconciliationLine from './rows/ReconciliationLine';
import ReconciliationTracker from '@/components/trackers/ReconciliationTracker';
import formRules from '@/mixins/formRules';
import miscServices from '@/services/misc';
import _ from 'lodash';
import moment from 'moment';

export default {
  name: 'reconciliation-detail',
  mixins: [formRules],
  data: () => ({
    isLoading: true,
    isFormValid: false,
    isSaving: false,
    showDialog: false,
    labelClass: 'body-2 grey--text',
    valueClass: 'text-right font-weight-bold',
    rec: null,
    recCopy: {},
    lines: [],
  }),
  computed: {
    totalDebits () {
      return _.sumBy(this.lines.filter(l => l.reconciliation), 'debit')
    },
    totalCredits () {
      return _.sumBy(this.lines.filter(l => l.reconciliation), 'credit')
    },
    toolbarData () {
      return this.$store.state.icons
    },
    basicsData () {
      let data = []
      if (this.rec) {
        data.push({ text: 'Account', value: this.rec.account_obj.name })
        data.push({ text: 'From date', value: this.$options.filters.shortDate(this.rec.from_date) })
        data.push({ text: 'To date', value: this.$options.filters.shortDate(this.rec.to_date) })
        data.push({ text: 'Statement End balance', value: this.$options.filters.decMoney(this.rec.end_balance) })
      }
      return data
    },
    cpmBalance () {
      return this.rec.opening_balance + this.totalDebits - this.totalCredits
    },
    diff () {
      return this.cpmBalance - this.rec.end_balance
    }
  },
  mounted () {
    this.getData()
  },
  methods: {
    async getData () {
      this.isLoading = true
      this.rec = await reconciliationsAPI.getReconciliation(this.$route.params.id);
      let includedLines = await reconciliationsAPI.getReconciledLines(this.rec.id);
      let openLines = []
      if (!this.rec.is_locked) {
        openLines = await reconciliationsAPI.getUnreconciledLines(this.rec.account);
      }
      this.lines = _.orderBy(_.concat(includedLines, openLines), 'journal_entry_obj.posting_date')
      this.isLoading = false
    },
    updateLine (line) {
      this.lines = miscServices.replaceObject(this.lines, line);
    },
    editRec () {
      this.recCopy = _.cloneDeep(this.rec)
      this.showDialog = true
    },
    async updateRec () {
      this.isSaving = true;
      this.rec = await reconciliationsAPI.updateReconciliation(this.recCopy.id, this.recCopy);
      this.showDialog = false;
      this.isSaving = false;

      const tracker = {
          'tracker_data': {
            'description': 'Reconciliation details updated',
            'taken_by': this.$store.state.auth.profile.id,
            'action_date': moment().format('YYYY-MM-DD').toString(),
          },
          'reconciliation': this.rec.id
      }

      const res = await trackerAPI.createReconciliationTracker(tracker);
      this.$refs.actionTracker.pushItem(res);
    },
    async toggleLock () {
      this.rec.is_locked = !this.rec.is_locked;
      reconciliationsAPI.updateReconciliation(this.rec.id, this.rec);

      let description = '';
      description = this.rec.is_locked ? 'Reconciliation locked' : 'Reconciliation UN-locked';

      const tracker = {
          'tracker_data': {
            'description': description,
            'taken_by': this.$store.state.auth.profile.id,
            'action_date': moment().format('YYYY-MM-DD').toString(),
          },
          'reconciliation': this.rec.id
      }

      const res = await trackerAPI.createReconciliationTracker(tracker);
      this.$refs.actionTracker.pushItem(res);
      this.getData();
    }
  },
  components: {
    'rec-line': ReconciliationLine,
    'reconciliation-tracker': ReconciliationTracker
  }
}
</script>
